import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api.js';

/**
 * Parcours de signature dans l'iframe Youtrust, en flux ordonné.
 *
 * Le point clé : on ne conserve JAMAIS les liens de signature côté application.
 * À chaque étape, on redemande au serveur l'état des signataires et le lien de
 * celui dont c'est le tour. Trois raisons :
 *
 *  1. En flux ordonné, le lien du second signataire n'existe pas avant que le
 *     premier ait signé — un lien mémorisé à l'activation serait null.
 *  2. Les liens expirent. Un lien récupéré au bon moment vaut mieux qu'un lien
 *     stocké quinze minutes plus tôt.
 *  3. Reprise : après une coupure réseau ou un téléphone déchargé, le même appel
 *     redonne l'état exact du parcours. Aucune annulation, aucune re-signature.
 */

const LIBELLES_ROLE = {
  etna: 'Représentant Etna France',
  maitreOeuvre: "Maître d'œuvre",
  maitreOuvrage: "Maître d'ouvrage",
  proprietaire: 'Propriétaire',
  representant: 'Représentant du client',
};

export function Signature({ ficheId, phase = 'reception', sandbox, onTermine, onErreur }) {
  const [signataires, setSignataires] = useState(null);
  const [etat, setEtat] = useState('chargement'); // chargement | pret | transition | termine | refusee | echec
  const [message, setMessage] = useState(null);
  const instance = useRef(null);
  const tentatives = useRef(0);

  const enCours = signataires?.find((s) => s.etat === 'pret') ?? null;

  /** Interroge le serveur et décide de l'écran à afficher. */
  const rafraichir = useCallback(async () => {
    try {
      const { signataires: liste } = await api.liensSignature(ficheId, phase);
      setSignataires(liste);

      if (liste.every((s) => s.etat === 'signe')) {
        setEtat('termine');
        return;
      }
      if (liste.some((s) => s.etat === 'pret')) {
        tentatives.current = 0;
        setEtat('pret');
        return;
      }
      const indisponible = liste.find((s) => s.etat === 'indisponible');
      if (indisponible) {
        setEtat('echec');
        setMessage(`Signataire inaccessible (${LIBELLES_ROLE[indisponible.role] ?? indisponible.role}).`);
        return;
      }
      // Personne n'est prêt et personne n'est en échec : Youtrust est en train de
      // basculer le tour au signataire suivant. C'est court, mais pas instantané.
      if (tentatives.current < 6) {
        tentatives.current += 1;
        setEtat('transition');
        setTimeout(rafraichir, 1500);
      } else {
        setEtat('echec');
        setMessage("Le signataire suivant n'a pas été activé par le service de signature.");
      }
    } catch (err) {
      setEtat('echec');
      setMessage(
        navigator.onLine
          ? err.message
          : 'Pas de réseau. La fiche et les signatures déjà apposées sont conservées : '
            + 'reprenez dès que la connexion revient.',
      );
    }
  }, [ficheId, phase]);

  useEffect(() => { rafraichir(); }, [rafraichir]);

  // Reprise automatique au retour du réseau : le technicien n'a rien à comprendre.
  useEffect(() => {
    const auRetour = () => { if (etat === 'echec') rafraichir(); };
    window.addEventListener('online', auRetour);
    return () => window.removeEventListener('online', auRetour);
  }, [etat, rafraichir]);

  // Montage de l'iframe pour le signataire dont c'est le tour.
  useEffect(() => {
    if (etat !== 'pret' || !enCours?.signatureLink) return undefined;
    if (typeof window.Yousign !== 'function') {
      onErreur('SDK de signature indisponible — vérifier le chargement du script.');
      return undefined;
    }

    if (instance.current) instance.current.removeMessageListener();

    const ys = new window.Yousign({
      signatureLink: enCours.signatureLink,
      iframeContainerId: 'conteneur-iframe',
      isSandbox: sandbox,
      // Nécessaire à la validation du domaine autorisé côté Youtrust.
      iframeAttributes: { referrerPolicy: 'strict-origin-when-cross-origin' },
    });
    instance.current = ys;

    ys.onSuccess(() => {
      // Un traitement interne suit l'événement côté Youtrust : on laisse le tour
      // basculer avant de redemander l'état. Et on ne se fie pas à cet événement
      // pour conclure : seul le serveur dit qui a signé.
      setEtat('transition');
      setTimeout(rafraichir, 1500);
    });

    ys.onDeclined(() => setEtat('refusee'));
    ys.onError((data) => {
      setEtat('echec');
      setMessage(`Erreur pendant la signature : ${data?.error ?? 'cause inconnue'}`);
    });

    return () => ys.removeMessageListener();
  }, [etat, enCours?.signatureLink, sandbox, rafraichir, onErreur]);

  useEffect(() => { if (etat === 'termine') onTermine(); }, [etat, onTermine]);

  // Toutes les signatures sont posées. `onTermine` fait basculer l'application vers
  // son écran de confirmation ; on affiche ce court état le temps de cette bascule.
  // Sans cette garde, le rendu final plus bas déréférencerait `enCours` (désormais
  // null puisque plus personne n'est « prêt ») et laisserait une page blanche.
  if (etat === 'termine') {
    return (
      <div className="etat succes">
        <div className="titre">Signature terminée</div>
        <p className="sous-titre">Enregistrement du document…</p>
      </div>
    );
  }

  if (etat === 'refusee') {
    return (
      <div className="etat">
        <div className="titre">Signature refusée</div>
        <p className="sous-titre">
          La réception n'est pas prononcée. La fiche reste enregistrée et le refus est
          journalisé. Prévenir le conducteur de travaux.
        </p>
      </div>
    );
  }

  if (etat === 'chargement' || etat === 'transition') {
    const dejaSignes = signataires?.filter((s) => s.etat === 'signe').length ?? 0;
    return (
      <div className="etat">
        <div className="titre">
          {etat === 'chargement' ? 'Préparation de la signature…' : 'Signature suivante…'}
        </div>
        <p className="sous-titre">
          {dejaSignes > 0
            ? `${dejaSignes} signature${dejaSignes > 1 ? 's' : ''} déjà enregistrée${dejaSignes > 1 ? 's' : ''}. Ne fermez pas cet écran.`
            : 'Ne fermez pas cet écran.'}
        </p>
      </div>
    );
  }

  if (etat === 'echec') {
    return (
      <div className="etat">
        <div className="titre">Signature interrompue</div>
        <p className="sous-titre">{message}</p>
        <p className="sous-titre">
          Rien n'est perdu : les signatures déjà apposées restent valables, et la
          reprise ne fera pas re-signer ceux qui ont déjà signé.
        </p>
        <button
          type="button"
          className="bouton-reprise"
          onClick={() => { setEtat('chargement'); rafraichir(); }}
        >
          Reprendre la signature
        </button>
      </div>
    );
  }

  return (
    <>
      <h2>{LIBELLES_ROLE[enCours.role] ?? 'Signature'}</h2>
      <p className="sous-titre">
        {enCours.nomComplet} — un code de confirmation est envoyé par SMS au numéro saisi.
        {signataires.length > 1 && (
          ` Signataire ${signataires.filter((s) => s.etat === 'signe').length + 1} sur ${signataires.length}.`
        )}
      </p>
      <div id="conteneur-iframe" />
    </>
  );
}
